import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ProductDataComponent } from '../product-data/product-data.component';
import { LegalChecksComponent } from '../legal-checks/legal-checks.component';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InsuranceDetailsComponent } from '@base/components/insurance-details/insurance-details.component';
import { PersonDataComponent } from '@base/components/person-data/person-data.component';
import { PaymentDataComponent } from '@base/components/payment-data/payment-data.component';
import {
  BrokernetLanguageType,
  BrokernetProductName,
  Bundesland,
  PaymentPeriod,
  PaymentType,
  PublicPersonCreateDto,
  PublicServoBikeContractCreateDto,
  PublicServoBikeLegalChecksDto,
  PublicServoBikeProductDetailsDto,
  ServoBikeProductType,
  ServoBikeVersicherungspraemieCalcDto
} from '@base/backend';
import { BikeFormModel, createBikeForm } from '../../factories/bike-form.factory';
import { checkForPaymentInfoInUrl, checkForPersonDataInUrl } from '@base/app/shared/utils/check-for-data-in-url.helper';
import { BikeService } from '../../services/bike.service';
import { map, Observable, startWith } from 'rxjs';
import { getAdvertiserInfo, getPublicLinkCustomerDto } from '@base/app/shared/utils/dto-builder.helper';
import { PaymentPeriodTranslations } from '@base/models/enum/payment-period-type.enum';
import { ServoProductNameTranslations } from '@base/models/enum/insurance-type.enum';
import { BikeProductTypeTranslations } from '@base/models/enum/bike-product-type.enum';

@Component({
  selector: 'app-bike-form-wrapper',
  standalone: true,
  imports: [
    ProductDataComponent,
    InsuranceDetailsComponent,
    PersonDataComponent,
    PaymentDataComponent,
    LegalChecksComponent,
    LegalChecksComponent,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './bike-form-wrapper.component.html',
  styleUrl: './bike-form-wrapper.component.scss'
})
export class BikeFormWrapperComponent implements OnInit {
  readonly bikeService = inject(BikeService);

  @Input() price!: number;
  @Input() isEbike: boolean | null = null;
  @Input() servoAdvertiserId: string | null = null;
  @Input() bikeProductType: ServoBikeProductType | null = null;
  @Input() isPublic: boolean = false;

  @Output() submitted = new EventEmitter<void>();
  @Output() navigateBack = new EventEmitter<{ isEbike: boolean | null, price: number }>();

  isLoading = false;

  form!: FormGroup<BikeFormModel>;

  isRedirectFromAdminPanel = window.location.href.includes('premium') && window.location.href.includes('price');

  ngOnInit() {
    this.form = createBikeForm();

    //If we ge redirected from the admin page we dont have a ServoBikeProductTypeEnum
    if (this.bikeProductType) {
      this.form.controls.product.controls.bikeProductType.setValue(this.bikeProductType);
    } else {
      this.form.controls.product.controls.bikeProductType.setValue(ServoBikeProductType.StandardBikeEbike);
    }

    this.form.controls.product.controls.isEbike.setValue(this.isEbike);
    this.checkForDataInUrl();

    this.form.valueChanges
      .pipe(
        startWith(this.form.value)
      )
      .subscribe(() => this.getPremium());
  }

  checkForDataInUrl(): void {
    const url: URL = new URL(window.location.href);
    const personForm = this.form.controls.person.controls;
    const paymentForm = this.form.controls.payment.controls;

    checkForPersonDataInUrl(url, personForm);
    checkForPaymentInfoInUrl(url, paymentForm);
  }

  getInsuranceDetailsTitle(): string {
    const appendix = this.bikeProductType ? ` mit ${BikeProductTypeTranslations.getTypeTranslation(this.bikeProductType)}` : '';
    return `${ServoProductNameTranslations.getTypeTranslation(BrokernetProductName.ServoBikeEBike)}${appendix}`;
  }

  getPremium(): Observable<number> {
    const divider = this.getPaymentDivider();
    const productControls = this.form.controls.product.controls;

    const dto: ServoBikeVersicherungspraemieCalcDto = {
      isEbike: productControls.isEbike.value ?? undefined,
      bikeProductType: productControls.bikeProductType.value ?? undefined,
      kaufpreisInEur: this.price
    };

    return this.bikeService.calculateBikePraemie(dto).pipe(
      map((result) => {
        if (result.versicherungspraemieInEur) {
          return result.versicherungspraemieInEur / divider;
        }

        return 0;
      })
    );
  }

  getPaymentDivider(): number {
    switch (this.form.controls.payment.controls.paymentPeriod.value) {
      case PaymentPeriod.Yearly:
        return 1;

      case PaymentPeriod.Halfyearly:
        return 2;

      case PaymentPeriod.Quarterly:
        return 4;

      case PaymentPeriod.Monthly:
        return 12;

      default:
        return 1;
    }
  }

  getPremiumIntervalText(): string {
    let value = this.form.controls.payment.controls.paymentPeriod.value;

    if (value === null) {
      value = PaymentPeriod.Monthly;
    }

    return PaymentPeriodTranslations.getTypeTranslation(value);
  }

  getIpidLink(): string {
    if (this.bikeProductType === ServoBikeProductType.Diebstahl) {
      return '/assets/IPID Bike Diebstahl.pdf';
    } else if (this.bikeProductType === ServoBikeProductType.KompaktPaket) {
      return '/assets/IPID Bike Kompakt.pdf';
    } else if (this.bikeProductType === ServoBikeProductType.PremiumPaket) {
      return '/assets/IPID Bike Premiumpaket.pdf';
    }

    return 'https://myservo.at/wp-content/uploads/2022/04/IPID-ID-GO_Bike_ab-01.04.22_final.pdf';
  }

  getAvbLink(): string {
    if (this.bikeProductType) {
      return '/assets/AVB Bike Haendler.pdf';
    }

    return 'https://myservo.at/wp-content/uploads/2024/03/AVB_Bike_FINAL_ab-04.2024.pdf';
  }

  submit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;

    const dto: PublicServoBikeContractCreateDto = {
      publicPerson: this.isPublic ? this.getPublicPersonCreateDto() : undefined,
      publicProductDetails: this.getProductDetailsDto(),
      publicLegalCheck: this.getLegalChecksDto(),
      publicBrokerAdvertiserInfo: this.isPublic ? getAdvertiserInfo(this.servoAdvertiserId) : undefined,
      publicLinkCustomerDto: this.isPublic ? undefined : getPublicLinkCustomerDto()
    };

    this.bikeService.submitBikeContract(dto)
      .subscribe({
        next: () => this.submitted.emit(),
        complete: () => this.isLoading = false
      });
  }

  private getPublicPersonCreateDto(): PublicPersonCreateDto {
    const rawPersonData = this.form.controls.person.getRawValue();
    const paymentData = this.form.controls.payment.getRawValue();

    return {
      firstname: rawPersonData.firstname!,
      lastname: rawPersonData.lastname!,
      publicAddressCreateDto: {
        city: rawPersonData.address.city!,
        country: rawPersonData.address.country!,
        houseNumber: rawPersonData.address.houseNumber!,
        street: rawPersonData.address.street!,
        zip: rawPersonData.address.zip!,
        bundesland: Bundesland.Oberoesterreich
      },
      publicContactInfoCreateDto: {
        email: rawPersonData.email!,
        phone: rawPersonData.phone
      },
      publicPaymentInfoCreateDto: {
        iban: paymentData.iban!,
        paymentPeriod: paymentData.paymentPeriod!,
        paymentType: PaymentType.Sepa
      },
      brokernetLanguageType: BrokernetLanguageType.De
    }
  }

  private getProductDetailsDto(): PublicServoBikeProductDetailsDto {
    const rawProductData = this.form.controls.product.getRawValue();

    return {
      brand: rawProductData.brand!,
      model: rawProductData.model!,
      serialNumber: rawProductData.serialNumber!,
      publicServoBikeVersicherungspraemieCalcDto: {
        isEbike: rawProductData.isEbike!,
        bikeProductType: rawProductData.bikeProductType!,
        kaufpreisInEur: this.price!
      }
    };
  }

  private getLegalChecksDto(): PublicServoBikeLegalChecksDto {
    const rawLegalChecksData = this.form.controls.legalCheck.getRawValue();

    return {
      hasAcceptedGeneralTermsAndConditions: rawLegalChecksData.hasAcceptedGeneralTermsAndConditions,
      hasAcceptedPrivacyPolicy: rawLegalChecksData.hasAcceptedPrivacyPolicy,
      hasAcknowledgedRightOfWithdrawal: rawLegalChecksData.hasAcknowledgedRightOfWithdrawal,
      hasAgreedToElectronicCommunication: rawLegalChecksData.hasAgreedToElectronicCommunication,
      hasAuthorizedSepaDirectDebit: rawLegalChecksData.hasAuthorizedSepaDirectDebit,
      hasConfirmedProductMeetsWishesAndNeeds: rawLegalChecksData.hasConfirmedProductMeetsWishesAndNeeds,
      hasReadInsuranceProductInformationSheet: rawLegalChecksData.hasReadInsuranceProductInformationSheet,
      hatKeinErsatzFuerHaftpflichtVersicherungAkzeptiert: rawLegalChecksData.hasIsHaftpflichtVersicherungNotReplacedConfirmed
    };
  }
}
